import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import {
  TDate,
  TREQUEST_BULK_NPT_CREATE,
  TREQUEST_LINES_NPT_DETAILS,
  TREQUEST_NPT_CREATE,
  TREQUEST_NPT_DELETE,
  TREQUEST_NPT_EDIT,
  TREQUEST_NPT_REASONS_LIST,
  TREQUEST_NPT_SUBCATEGORY_DELETE,
  TREQUEST_WORKSTATIONS_NPT_DETAILS,
  TREQUEST_WORKSTATION_NPTS,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { catchError, finalize, lastValueFrom, map, of, tap } from 'rxjs';
import { TMachineMaintenanceState } from '../reducers';
import * as TNptActions from './npt.actions';
import * as TNptSelections from './npt.selectors';
import { NPT_REASONS_UPLOAD_DEFAULT_PAGINATION_OPTION } from './npt.models';
import { TUserPaginationOption } from '@monorepo/ui';

@Injectable({ providedIn: 'root' })
export class NptFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private machineMaintenanceDataService: MachineMaintenanceDataService,
  ) {}

  fetchNptLinesDetails(payload: TREQUEST_LINES_NPT_DETAILS) {
    return this.store.dispatch(new TNptActions.GetNptLinesDetails(payload));
  }

  getNptLinesDetails() {
    return this.store.pipe(select(TNptSelections.getNptLinesDetails));
  }

  fetchNptWorkstationsDetails(payload: TREQUEST_WORKSTATIONS_NPT_DETAILS) {
    this.store.dispatch(new TNptActions.GetNptWorkstationsDetails(payload));
  }

  fetchWorkstationNpts(payload: TREQUEST_WORKSTATION_NPTS) {
    this.store.dispatch(new TNptActions.GetWorkstationsNpts(payload));
  }

  getWorkstationNpts() {
    return this.store.pipe(select(TNptSelections.getWorkstationNpts));
  }

  getNptWorkstationGroupsDetails() {
    return this.store.pipe(select(TNptSelections.getNptWorkstationGroupsDetails));
  }

  getNptReasonsWithDisplayName() {
    return this.store.pipe(select(TNptSelections.getNptReasonsWithDisplayName));
  }

  async createNpt(payload: TREQUEST_NPT_CREATE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.createNpt(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }
  async createNptInPast(payload: TREQUEST_NPT_CREATE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.createNptInPast(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }
  async createBulkNpt(payload: TREQUEST_BULK_NPT_CREATE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.createBulkNpt(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async editNpt(payload: TREQUEST_NPT_EDIT) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.editNpt(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async editNptInPast(payload: TREQUEST_NPT_EDIT) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.editNptInPast(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async deleteNpt(payload: TREQUEST_NPT_DELETE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.deleteNpt(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  getSelectedDate() {
    return this.store.pipe(select(TNptSelections.getSelectedDate));
  }

  setSelectedDate(selectedDate: TDate) {
    return this.store.dispatch(new TNptActions.NptSetSelectedDate(selectedDate));
  }

  async deleteNptInPast(payload: TREQUEST_NPT_DELETE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.deleteNptInPast(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  fetchNptReasonsList(payload: TREQUEST_NPT_REASONS_LIST, resetPagination?: boolean) {
    return this.store.dispatch(new TNptActions.FetchNptReasonsList(payload, resetPagination));
  }

  resetNptReasonsList() {
    return this.fetchNptReasonsList(
      {
        itemsPerPage: NPT_REASONS_UPLOAD_DEFAULT_PAGINATION_OPTION.pageSize,
        page: NPT_REASONS_UPLOAD_DEFAULT_PAGINATION_OPTION.pageNo,
        selectedFilters: [],
      },
      true,
    );
  }

  getNptReasonsList() {
    return this.store.pipe(select(TNptSelections.getNptReasonsList));
  }

  async deleteNptReasonSubcategory(payload: TREQUEST_NPT_SUBCATEGORY_DELETE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.deleteNptReasonSubcategory(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  getNptReasonsListPaginationOption() {
    return this.store.pipe(select(TNptSelections.getNptReasonsListPaginationOption));
  }

  setNptReasonsListPaginationOption(payload: TUserPaginationOption) {
    return this.store.dispatch(new TNptActions.SetReasonsListPaginationOption(payload));
  }

  async getFactoryLevelNpts(date: TDate) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.getFactoryLevelNpts(date).pipe(
        tap((res) => this.store.dispatch(new TNptActions.SetNptReasons(res.nptReasons))),
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }
}
